import {
  createContext,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from "react";

export type Language = "en" | "zh";

const LANGUAGE_STORAGE_KEY = "catiechess.language";

export const i18nCopy = {
  en: {
    shell: {
      tagline: "",
      nav: {
        play: "Play",
        analysis: "Analysis",
        livestream: "Livestream",
        devlog: "Dev log",
        about: "About",
      },
      signUp: "Sign up",
      checking: "...",
      switchLabel: "Switch to Chinese",
      currentLanguage: "EN",
    },
    home: {
      hero: {
        title: "Catie Chess",
        statement: "Chess is Language",
        subtitle: "Sequence-based. No search.",
        pillars: [
          "Human-like moves.",
          "Human-like plans.",
          "Human-like mistakes.",
        ],
      },
      faqLabel: "Homepage questions",
      faqItems: [
        {
          title: "What is Catie?",
          body: [
            "Catie is a GPT-style chess AI that plays without engine search.",
            "Like a language model predicts the next word, Catie predicts the next chess move as a token from the moves that came before.",
            "No Stockfish.",
            "No tree search.",
            "No looking millions of positions ahead.",
            "Just sequence prediction.",
          ],
        },
        {
          title: "How does Catie play chess?",
          body: [
            "Catie reads a game as a sequence: move -> move -> move -> next move.",
            "Instead of evaluating only the current board, it learns patterns from the full history of the game: openings, plans, mistakes, style, and momentum.",
            "That means Catie does not choose the best engine move.",
            "It generates the next move directly from what it has learned.",
          ],
        },
        {
          title: "Does Catie calculate like a normal chess engine?",
          body: [
            "No - not in the engine sense.",
            "Traditional engines search many possible futures before choosing a move.",
            "Catie does not run engine search, does not use Stockfish, and does not look ahead through move trees at play time.",
            "It plays by neural prediction: given this game so far, what move comes next?",
          ],
        },
        {
          title: "How is Catie trained?",
          body: [
            "Catie starts with one base model trained on chess games written as move-token sequences.",
            "Then we use SFT - supervised fine-tuning - to create different versions of Catie with different behaviors.",
            "For example, we can fine-tune models to play more aggressively, more solidly, more beginner-like, more master-like, or with different styles and personalities.",
            "Same base model. Different fine-tuned players.",
          ],
        },
        {
          title: "Join the experiment",
          body: [
            "Play against Catie. Find weird moves. Share your games. Compare different versions.",
            "Help us test what GPT-style chess models can and cannot do.",
            "Join our Discord to follow new models, experiments, and community challenges.",
          ],
        },
      ],
      community: {
        label: "Join Discord",
        eyebrow: "Community server",
        detail: "New Catie models, experiments, and challenges.",
        open: "Open",
      },
    },
    profile: {
      loading: "Loading profile...",
      notFound: "This profile does not exist.",
      failed: "Failed to load profile.",
      editProfile: "Edit profile",
      bio: "Bio",
      credentialsLabel: "Public chess credentials",
      achievements: {
        title: "Achievements",
        unlocked: "Unlocked",
        locked: "Locked",
        nextTargets: "Next targets",
        expand: "Expand",
        collapse: "Collapse",
        filters: "Achievement filters",
        empty: "No unlocked achievements yet.",
        detailClose: "Close achievement detail",
        details: "details",
        statusUnlocked: "UNLOCKED",
        statusInProgress: "NEXT TARGET",
        statusLocked: "LOCKED",
        progressOf: "of",
      },
      activity: {
        viewsLabel: "Profile activity views",
        activity: "Activity",
        games: "Games",
        game: "Game",
        et: "ET",
        loading: "Loading activity",
        empty: "No recorded activity yet.",
        loadingMore: "Loading more activity...",
        failed: "Failed to load activity.",
        livestreamGuess: "livestream guess",
        livestreamGuesses: "livestream guesses",
        did: "Did",
        right: "right",
        wrong: "wrong",
        started: "Started",
        modelGame: "model game",
        modelGames: "model games",
        provided: "Provided",
        modelMove: "model move",
        modelMoves: "model moves",
        finished: "Finished",
        updatedProfile: "Updated profile",
        updatedProfileTimes: "Updated profile {count} times",
      },
      modelGames: {
        empty: "No model games yet.",
        loadingMore: "Loading more games...",
        failed: "Failed to load games.",
        white: "White",
        black: "Black",
        ply: "ply",
        live: "Live",
        final: "Final",
      },
      edit: {
        loading: "Loading editor...",
        eyebrow: "Edit profile",
        uploadPhoto: "Upload photo",
        fideRating: "FIDE rating",
        title: "Title",
        chinaTitle: "China title",
        bio: "Bio",
        saving: "Saving...",
        save: "Save profile",
        cancel: "Cancel",
        failed: "failed to save profile",
      },
    },
    auth: {
      loginTitle: "Welcome back to Catie Chess",
      signupTitle: "You are so close from contributing to Catie Chess",
      email: "Email",
      username: "Username",
      password: "Password",
      confirmPassword: "re-enter password",
      signIn: "Sign in",
      signingIn: "Signing in...",
      createAccount: "Create account",
      creating: "Creating...",
      newUser: "new to catie?",
      signupLink: "sign up!",
      existingUser: "have an account?",
      loginLink: "log in!",
      securityRequired: "security check required",
      loginFailed: "login failed",
      signupFailed: "signup failed",
      oauth: {
        google: "Continue with Google",
        lichess: "Continue with Lichess",
      },
    },
    play: {
      floorEyebrow: "",
      floorTitle: "WHO TO CHALLENGE",
      legends: "Legends",
      crowds: "Crowds",
      ctm: "CTM",
      standard: "Standard",
      engineSwitch: {
        label: "Catie version",
        versions: {
          catie_1_0: {
            label: "Catie1.0",
            meta: "Policy",
          },
          catie_2_1: {
            label: "Catie2.0",
            meta: "MCTS",
          },
          catie_3_0: {
            label: "Catie3.0",
            meta: "CTM",
          },
        },
      },
      resume: {
        eyebrow: "Live room",
        title: "One active game at a time",
        body: "End your game before starting a new one!",
        model: "Model",
        state: "State",
        moves: "Moves",
        action: "Resume game",
      },
      modal: {
        dismiss: "Dismiss",
        versionLine: "Version line",
        models: "models",
        protocol: "Challenge protocol",
        engine: "Catie",
        line: "Line",
        seat: "Seat",
        difficulty: "Difficulty",
        clock: "Clock",
        seatSelection: "Seat selection",
        white: "White",
        black: "Black",
        hardness: "Hardness",
        timeControl: "Time control",
        time: "Time",
        increment: "Increment",
        min: "min",
        sec: "sec",
        cancel: "Cancel",
        starting: "Starting...",
        launch: "Launch challenge",
      },
      opening: {
        entryLabel: "Start from position",
        standardStart: "Standard start",
        toMove: "{side} to move",
        set: "Set",
        edit: "Edit",
        clear: "Clear",
        dismiss: "Dismiss",
        eyebrow: "Opening seed",
        title: "Start from position",
        boardTab: "Board",
        pgnTab: "PGN",
        pgnPlaceholder: "Paste one PGN game. Variations are ignored; choose a mainline move as the start.",
        loadPgn: "Load PGN",
        parsing: "Parsing...",
        moveLine: "Move line",
        empty: "No opening moves yet.",
        flip: "Flip",
        undo: "Undo",
        reset: "Reset",
        usePosition: "Use this position",
      },
      difficulty: {
        easy: "Easy",
        median: "Median",
        hard: "Hard",
      },
    },
    livestream: {
      label: "Livestream guess",
      title: "livestream guess",
      syncing: "SYNCING",
      retry: "Retry",
      feedUnavailable: "Feed unavailable",
      inactive: "livestream source is not active",
      statuses: {
        syncing: "SYNCING",
        retry: "RETRY",
        paused: "PAUSED",
        review: "REVIEW",
        matched: "MATCHED",
        wrong: "WRONG",
        live: "LIVE",
      },
      timer: {
        latest: "Latest",
        paused: "Paused",
        pause: "Pause",
      },
      authPrompt: "Sign in to lock your score",
      signIn: "Sign in",
      help: {
        close: "Close Livestream Guess explanation",
        eyebrow: "How it works",
        title: "Predict the model move before it appears.",
        body: "Pick one legal move, lock it before the countdown ends, then compare it with Catie's real next move. Signed-in guesses are saved to your profile score.",
      },
      confirm: {
        lock: "Lock",
        to: "to",
        question: "?",
        enter: "(or press Enter)",
        cancel: "Cancel",
        locking: "Locking",
        lockIn: "Lock in",
      },
      result: {
        final: "Final result",
        whiteWins: "White wins",
        blackWins: "Black wins",
        draw: "Draw",
      },
      room: {
        info: "Info",
        stockfish: "Stockfish",
        white: "White",
        black: "Black",
        lichess: "Analysis on Lichess",
        open: "Open",
        moves: "Moves",
      },
      stockfishLocks: {
        noPosition: "No position yet",
        lockingMove: "Locking move",
        currentLive: "Current live position locked",
        afterGuess: "Stockfish opens after the guess is recorded.",
        afterFirstMove: "Stockfish opens after the first visible move.",
        reviewPrevious: "Review previous",
      },
    },
    analysis: {
      workspace: "Analysis workspace",
      stockfish: "Stockfish analysis",
      locked: "LOCKED",
      turnStockfishOff: "Turn Stockfish off",
      turnStockfishOn: "Turn Stockfish on",
      expandLine: "Expand engine line",
      top3: "Top 3 lines",
      moreLines: "More lines",
      engineOff: "ENGINE OFF",
      engineUnavailable: "ENGINE UNAVAILABLE",
      calculating: "CALCULATING",
      whiteBetter: "WHITE BETTER",
      blackBetter: "BLACK BETTER",
      even: "EVEN",
      controls: "Analysis playback controls",
      initial: "Initial position",
      previous: "Previous move",
      next: "Next move",
      latest: "Latest position",
      flip: "Flip",
      copyFen: "Copy full FEN",
      copied: "Copied",
      failed: "Failed",
      copy: "Copy",
      moveHistory: "Move history",
      modelChoice: "Model Choice",
      model: "Model",
      top: "Top",
      off: "Off",
      on: "On",
      queued: "Queued",
      running: "Running",
      done: "Done",
      ready: "Ready",
      offline: "Offline",
      switchOn: "Switch on.",
      timedOut: "Model choice timed out",
      unavailable: "Model choice unavailable",
      turnModelOff: "Turn model choice off",
      turnModelOn: "Turn model choice on",
      topChoices: "Top K choices",
      playModelChoice: "Play model choice",
    },
    error: {
      modelCrashedTitle: "Model crashed",
      modelCrashedMessage:
        "Model crashed. The repair team will handle it immediately.",
    },
    devlog: {
      title: "Dev log",
      statusLabel: "Dev log status",
      latest: "Latest entry",
      noPosts: "No entries yet",
      adminMode: "Admin editing is available on this account.",
      editorLabel: "Dev log editor",
      editorEyebrow: "Admin editor",
      newPost: "New update",
      editing: "Editing update",
      titleLabel: "Title",
      bodyLabel: "Post body",
      bodyPlaceholder: "Write the update. Plain text only for now.",
      clear: "Clear",
      publish: "Publish",
      save: "Save changes",
      saving: "Saving...",
      cancelEdit: "Cancel edit",
      adminOnly: "Admin permission is required.",
      saveFailed: "Failed to save post.",
      deleteFailed: "Failed to delete post.",
      failed: "Failed to load dev log.",
      feedLabel: "Dev log feed",
      loading: "Loading dev log...",
      empty: "No dev log posts yet.",
      updated: "Updated",
      edit: "Edit",
      delete: "Delete",
      confirmDelete: "Confirm delete",
      cancel: "Cancel",
      loadOlder: "Load older",
      loadingMore: "Loading older entries...",
    },
  },
  zh: {
    shell: {
      tagline: "",
      nav: {
        play: "对弈",
        analysis: "分析",
        livestream: "直播猜棋",
        devlog: "更新日志",
        about: "关于我们",
      },
      signUp: "注册",
      checking: "...",
      switchLabel: "切换到英文",
      currentLanguage: "中",
    },
    home: {
      hero: {
        title: "Catie Chess",
        statement: "国际象棋也是语言",
        subtitle: "不做任何计算。",
        pillars: [
          "像人一样走棋。",
          "像人一样做计划。",
          "也会犯像人一样的错。",
        ],
      },
      faqLabel: "首页问题",
      faqItems: [
        {
          title: "Catie 是什么？",
          body: [
            "Catie 是一个 GPT 风格的国际象棋 AI，下棋时不做引擎搜索。",
            "就像语言模型根据前文预测下一个词，Catie 会根据已经发生的走法，把下一步棋当成一个 token 直接预测出来。",
            "不用 Stockfish。",
            "不展开搜索树。",
            "不提前看上百万个局面。",
            "它只做序列预测。",
          ],
        },
        {
          title: "Catie 怎么下棋？",
          body: [
            "Catie 把一盘棋读成一个序列：走法 -> 走法 -> 走法 -> 下一步。",
            "它不是只看当前棋盘，而是从整盘棋的历史里学习开局、计划、失误、风格和局势惯性。",
            "所以 Catie 并不是在选择“引擎最优解”。",
            "它会根据学到的模式直接生成下一步。",
          ],
        },
        {
          title: "Catie 会像传统引擎一样计算吗？",
          body: [
            "不会，至少不是传统引擎意义上的计算。",
            "传统引擎会搜索大量未来变化，再选择一步。",
            "Catie 下棋时不跑引擎搜索、不使用 Stockfish，也不展开走法树。",
            "它做的是神经网络预测：给定这盘棋到目前为止的历史，下一步最像什么？",
          ],
        },
        {
          title: "Catie 是怎么训练的？",
          body: [
            "Catie 先用棋谱走法序列训练一个基础模型。",
            "之后我们用监督微调（SFT）做出不同行为版本。",
            "例如更激进、更稳健、更像初学者、更像大师，或者带有不同风格和个性的模型。",
            "同一个基础模型，可以微调成不同的棋手。",
          ],
        },
        {
          title: "加入这个实验",
          body: [
            "和 Catie 下棋，找奇怪的走法，分享你的对局，比较不同模型。",
            "一起测试 GPT 风格的国际象棋模型到底能做什么、不能做什么。",
            "加入QQ群，跟进新模型、实验和社区挑战。",
          ],
        },
      ],
      community: {
        label: "加入QQ群",
        eyebrow: "中文交流群",
        detail: "群号：553248693",
        open: "复制",
      },
    },
    profile: {
      loading: "正在加载个人主页...",
      notFound: "这个个人主页不存在。",
      failed: "个人主页加载失败。",
      editProfile: "编辑资料",
      bio: "简介",
      credentialsLabel: "公开棋手信息",
      achievements: {
        title: "成就",
        unlocked: "已解锁",
        locked: "未解锁",
        nextTargets: "下个目标",
        expand: "展开",
        collapse: "收起",
        filters: "成就筛选",
        empty: "还没有解锁成就。",
        detailClose: "关闭成就详情",
        details: "详情",
        statusUnlocked: "已解锁",
        statusInProgress: "下个目标",
        statusLocked: "未解锁",
        progressOf: "/",
      },
      activity: {
        viewsLabel: "个人主页动态视图",
        activity: "动态",
        games: "对局",
        game: "对局",
        et: "美东时间",
        loading: "正在加载动态",
        empty: "还没有记录动态。",
        loadingMore: "正在加载更多动态...",
        failed: "动态加载失败。",
        livestreamGuess: "次直播猜棋",
        livestreamGuesses: "次直播猜棋",
        did: "完成",
        right: "对",
        wrong: "错",
        started: "开始",
        modelGame: "盘模型对局",
        modelGames: "盘模型对局",
        provided: "提交",
        modelMove: "步模型对局走法",
        modelMoves: "步模型对局走法",
        finished: "完成",
        updatedProfile: "更新个人主页",
        updatedProfileTimes: "更新个人主页 {count} 次",
      },
      modelGames: {
        empty: "还没有和模型下过棋。",
        loadingMore: "正在加载更多对局...",
        failed: "对局加载失败。",
        white: "白方",
        black: "黑方",
        ply: "半步",
        live: "进行中",
        final: "结束",
      },
      edit: {
        loading: "正在加载编辑器...",
        eyebrow: "编辑资料",
        uploadPhoto: "上传头像",
        fideRating: "FIDE 等级分",
        title: "称号",
        chinaTitle: "中国称号",
        bio: "简介",
        saving: "正在保存...",
        save: "保存资料",
        cancel: "取消",
        failed: "资料保存失败",
      },
    },
    auth: {
      loginTitle: "欢迎回到 Catie Chess",
      signupTitle: "你离参与 Catie Chess 只差一步",
      email: "邮箱",
      username: "用户名",
      password: "密码",
      confirmPassword: "再次输入密码",
      signIn: "登录",
      signingIn: "正在登录...",
      createAccount: "创建账号",
      creating: "正在创建...",
      newUser: "第一次来 Catie？",
      signupLink: "注册",
      existingUser: "已经有账号？",
      loginLink: "登录",
      securityRequired: "请先完成安全检查",
      loginFailed: "登录失败",
      signupFailed: "注册失败",
      oauth: {
        google: "使用 Google 继续",
        lichess: "使用 Lichess 继续",
      },
    },
    play: {
      floorEyebrow: "",
      floorTitle: "选择对手",
      legends: "名人模型",
      crowds: "群体模型",
      ctm: "CTM",
      standard: "标准",
      engineSwitch: {
        label: "Catie 版本",
        versions: {
          catie_1_0: {
            label: "Catie1.0",
            meta: "策略",
          },
          catie_2_1: {
            label: "Catie2.0",
            meta: "MCTS",
          },
          catie_3_0: {
            label: "Catie3.0",
            meta: "CTM",
          },
        },
      },
      resume: {
        eyebrow: "进行中的对局",
        title: "一次只能保留一盘进行中的对局",
        body: "结束当前对局后，才能开始新的挑战。",
        model: "模型",
        state: "状态",
        moves: "走法",
        action: "继续对局",
      },
      modal: {
        dismiss: "关闭",
        versionLine: "版本分支",
        models: "个模型",
        protocol: "挑战设置",
        engine: "Catie",
        line: "分支",
        seat: "执棋",
        difficulty: "难度",
        clock: "时间",
        seatSelection: "选择执棋",
        white: "白棋",
        black: "黑棋",
        hardness: "强度",
        timeControl: "时间控制",
        time: "时间",
        increment: "加秒",
        min: "分钟",
        sec: "秒",
        cancel: "取消",
        starting: "正在开始...",
        launch: "开始挑战",
      },
      opening: {
        entryLabel: "指定开局",
        standardStart: "标准初始局面",
        toMove: "{side}走",
        set: "设置",
        edit: "编辑",
        clear: "清除",
        dismiss: "关闭",
        eyebrow: "开局起点",
        title: "指定开局",
        boardTab: "棋盘",
        pgnTab: "PGN",
        pgnPlaceholder: "粘贴一盘 PGN。系统只读取主线，你可以选择其中一步作为起点。",
        loadPgn: "载入 PGN",
        parsing: "解析中...",
        moveLine: "走法线",
        empty: "还没有开局走法。",
        flip: "翻转",
        undo: "撤回",
        reset: "重置",
        usePosition: "使用这个局面",
      },
      difficulty: {
        easy: "轻松",
        median: "标准",
        hard: "困难",
      },
    },
    livestream: {
      label: "直播猜棋",
      title: "直播猜棋",
      syncing: "同步中",
      retry: "重试",
      feedUnavailable: "直播源暂时不可用",
      inactive: "直播源暂时没有响应",
      statuses: {
        syncing: "同步中",
        retry: "重试",
        paused: "暂停",
        review: "复盘",
        matched: "猜中",
        wrong: "错误",
        live: "直播",
      },
      timer: {
        latest: "最新",
        paused: "暂停",
        pause: "暂停",
      },
      authPrompt: "登录后才能记录分数",
      signIn: "登录",
      help: {
        close: "关闭直播猜棋说明",
        eyebrow: "玩法说明",
        title: "在模型走出下一步之前，先猜它会怎么走。",
        body: "选择一步合法棋，在倒计时结束前锁定，然后和 Catie 的真实下一步对比。登录后的猜棋会记录到你的个人主页分数。",
      },
      confirm: {
        lock: "锁定",
        to: "到",
        question: "？",
        enter: "（也可以按 Enter）",
        cancel: "取消",
        locking: "锁定中",
        lockIn: "锁定",
      },
      result: {
        final: "最终结果",
        whiteWins: "白方获胜",
        blackWins: "黑方获胜",
        draw: "和棋",
      },
      room: {
        info: "信息",
        stockfish: "Stockfish",
        white: "白方",
        black: "黑方",
        lichess: "在 Lichess 分析",
        open: "打开",
        moves: "走法",
      },
      stockfishLocks: {
        noPosition: "还没有局面",
        lockingMove: "正在锁定走法",
        currentLive: "当前直播局面不能分析",
        afterGuess: "猜棋记录完成后，Stockfish 会打开。",
        afterFirstMove: "第一步出现后，Stockfish 会打开。",
        reviewPrevious: "查看前一步",
      },
    },
    analysis: {
      workspace: "分析工作台",
      stockfish: "Stockfish 分析",
      locked: "锁定",
      turnStockfishOff: "关闭 Stockfish",
      turnStockfishOn: "打开 Stockfish",
      expandLine: "展开引擎变化",
      top3: "前三条变化",
      moreLines: "更多变化",
      engineOff: "引擎已关闭",
      engineUnavailable: "引擎不可用",
      calculating: "计算中",
      whiteBetter: "白方更好",
      blackBetter: "黑方更好",
      even: "均势",
      controls: "分析回放控制",
      initial: "初始局面",
      previous: "前一步",
      next: "后一步",
      latest: "最新局面",
      flip: "翻转",
      copyFen: "复制完整 FEN",
      copied: "已复制",
      failed: "失败",
      copy: "复制",
      moveHistory: "走法记录",
      modelChoice: "模型选择",
      model: "模型",
      top: "前",
      off: "关",
      on: "开",
      queued: "排队中",
      running: "运行中",
      done: "完成",
      ready: "就绪",
      offline: "离线",
      switchOn: "打开后开始分析。",
      timedOut: "模型选择超时",
      unavailable: "模型选择暂时不可用",
      turnModelOff: "关闭模型选择",
      turnModelOn: "打开模型选择",
      topChoices: "Top K 选择",
      playModelChoice: "走出模型选择",
    },
    error: {
      modelCrashedTitle: "模型崩了",
      modelCrashedMessage: "模型服务暂时崩了，修复人员会马上处理。",
    },
    devlog: {
      title: "更新日志",
      statusLabel: "更新日志状态",
      latest: "最新一条",
      noPosts: "还没有内容",
      adminMode: "当前账号可以编辑更新日志。",
      editorLabel: "更新日志编辑器",
      editorEyebrow: "管理员编辑",
      newPost: "发布新动态",
      editing: "正在编辑",
      titleLabel: "标题",
      bodyLabel: "正文",
      bodyPlaceholder: "写下这次更新。目前只支持纯文本。",
      clear: "清空",
      publish: "发布",
      save: "保存修改",
      saving: "保存中...",
      cancelEdit: "取消编辑",
      adminOnly: "需要管理员权限。",
      saveFailed: "保存失败。",
      deleteFailed: "删除失败。",
      failed: "更新日志加载失败。",
      feedLabel: "更新日志列表",
      loading: "正在加载更新日志...",
      empty: "还没有更新日志。",
      updated: "已更新",
      edit: "编辑",
      delete: "删除",
      confirmDelete: "确认删除",
      cancel: "取消",
      loadOlder: "加载更早内容",
      loadingMore: "正在加载更早内容...",
    },
  },
} as const;

type I18nContextValue = {
  language: Language;
  copy: (typeof i18nCopy)[Language];
  setLanguage: (language: Language) => void;
  toggleLanguage: () => void;
};

const I18nContext = createContext<I18nContextValue | null>(null);

function readStoredLanguage(): Language {
  if (typeof window === "undefined") {
    return "en";
  }
  return window.localStorage.getItem(LANGUAGE_STORAGE_KEY) === "zh" ? "zh" : "en";
}

export function LanguageProvider({ children }: { children: ReactNode }) {
  const [language, setLanguageState] = useState<Language>(() => readStoredLanguage());

  const setLanguage = (nextLanguage: Language) => {
    setLanguageState(nextLanguage);
  };

  const toggleLanguage = () => {
    setLanguageState((current) => (current === "en" ? "zh" : "en"));
  };

  useEffect(() => {
    if (typeof window !== "undefined") {
      window.localStorage.setItem(LANGUAGE_STORAGE_KEY, language);
    }
    document.documentElement.lang = language === "zh" ? "zh-Hans" : "en";
  }, [language]);

  const value = useMemo<I18nContextValue>(
    () => ({
      language,
      copy: i18nCopy[language],
      setLanguage,
      toggleLanguage,
    }),
    [language],
  );

  return <I18nContext.Provider value={value}>{children}</I18nContext.Provider>;
}

export function useI18n() {
  const context = useContext(I18nContext);
  if (!context) {
    throw new Error("useI18n must be used inside LanguageProvider");
  }
  return context;
}
